import type {
  DragConstraint,
  DragUpdateOptions,
  DragUpdateResult,
} from "@project/emerge-types";
import type * as THREE from "three";
import {
  INTERIOR_MAX_SIZE as MAX_SIZE,
  INTERIOR_MIN_SIZE as MIN_SIZE,
} from "../../constants/dimensionLimits";
import { InteriorType } from "../../enum/InteriorType";
import type { FlightCaseSettings } from "../../interfaces/FlightCaseSettings";
import type { Rack19InteriorOptions } from "../../interfaces/Rack19InteriorOptions";
import {
  dimensionFromCornerDelta,
  gridStepFromOptions,
} from "../../utils/dragGrid";
import {
  HETomm,
  mmToHE,
  rackUnitHeight,
} from "../FlightCaseBlueprintCalculator";
import {
  cornerAxisBounds,
  cornerAxisPosition,
  cornerAxisSign,
} from "./cornerGeometry";
import type {
  CaseCornerPosition,
  CornerDragConstraintResult,
  CornerDragHandler,
} from "./types";

/** Tolerance (mm) for recognising a height as a whole number of rack units. */
const HE_EPSILON = 0.01;

/**
 * Snap a height to a whole rack unit when it already is one.
 *
 * Rack units are 45 mm apart, so the 10 mm drag grid cannot express them — it
 * would turn a 1 HE step into 40 or 50 mm and drift the height off the HE
 * ladder the sidebar edits in. Heights that land on an HE anchor are taken
 * exactly; everything else falls back to the grid.
 */
function resolveRackHeight(
  rawHeight: number,
  currentHeight: number,
  cornerDelta: number,
  gridStepMm: number,
): number {
  const units = (rawHeight - 5) / rackUnitHeight;
  const rounded = Math.round(units);
  if (rounded >= 1 && Math.abs(units - rounded) < HE_EPSILON) {
    return HETomm(rounded);
  }
  return dimensionFromCornerDelta(
    currentHeight,
    cornerDelta,
    MIN_SIZE,
    gridStepMm,
  );
}

/**
 * Drag handler for RACK19 interior type.
 * - Bottom corners: only resize depth (width is fixed at 465mm)
 * - Top corners: resize height with HE snap anchors
 */
export const Rack19CornerDragHandler: CornerDragHandler = {
  shouldEnable(settings: FlightCaseSettings): boolean {
    return settings.interiorOptions.type === InteriorType.RACK19;
  },

  buildConstraint(
    caseCornerPosition: CaseCornerPosition,
    position: THREE.Vector3,
    innerDimensions: THREE.Vector3,
    settings: FlightCaseSettings,
  ): CornerDragConstraintResult | null {
    const isBottom = caseCornerPosition.endsWith("Bottom");

    const rack19 = settings.interiorOptions as Rack19InteriorOptions;
    const currentWidth = rack19.width ?? 465; // Fixed 19" width
    const currentHeight = rack19.height ?? 0;
    const currentDepth = rack19.depth ?? innerDimensions.z;

    if (isBottom) {
      // Bottom corners: only resize depth (width is fixed at 465mm)
      const zSign = cornerAxisSign(position.z);
      const zRange = cornerAxisBounds(
        position.z,
        zSign,
        currentDepth,
        MIN_SIZE,
        MAX_SIZE,
      );

      const dragConstraint: DragConstraint = {
        type: "edge",
        axis: [0, 0, 1], // Z axis only (depth)
        bounds: {
          min: [position.x, position.y, zRange.lo],
          max: [position.x, position.y, zRange.hi],
        },
        snapAnchors: [],
        sourceOptionUuid: "interior_corner_resize_z",
      };

      return {
        dragConstraint,
        settingsContext: {
          caseCornerPosition,
          mode: "z",
          interiorType: "rack19",
          zSign,
          restZ: position.z,
          currentWidth,
          currentHeight,
          currentDepth,
        },
      };
    } else {
      // Top corners: resize height with HE snap anchors
      const yRange = cornerAxisBounds(
        position.y,
        1,
        currentHeight,
        MIN_SIZE,
        MAX_SIZE,
      );

      // Generate snap points at each HE (rack unit) increment. The corner moves
      // half as far as the height it drives, so the anchors sit at half the HE
      // spacing too — placing them a full HE apart would make a snap write
      // twice the height the label promises.
      const snapAnchors: DragConstraint["snapAnchors"] = [];
      for (let he = 1; he <= 100; he++) {
        const snapY = cornerAxisPosition(
          position.y,
          1,
          currentHeight,
          HETomm(he),
        );
        if (snapY >= yRange.lo && snapY <= yRange.hi) {
          snapAnchors.push({
            position: [position.x, snapY, position.z],
            label: `${he} HE`,
          });
        }
      }

      const dragConstraint: DragConstraint = {
        type: "edge",
        axis: [0, 1, 0], // Vertical axis
        bounds: {
          min: [position.x, yRange.lo, position.z],
          max: [position.x, yRange.hi, position.z],
        },
        snapAnchors,
        sourceOptionUuid: "interior_corner_resize_y",
      };

      return {
        dragConstraint,
        settingsContext: {
          caseCornerPosition,
          mode: "y",
          interiorType: "rack19",
          restY: position.y,
          currentWidth,
          currentHeight,
          currentDepth,
        },
      };
    }
  },

  updateSettings(
    newPosition: THREE.Vector3,
    settingsContext: Record<string, unknown>,
    currentSettings: FlightCaseSettings,
    options?: DragUpdateOptions,
  ): DragUpdateResult<FlightCaseSettings> | null {
    const mode = settingsContext.mode as "z" | "y";
    const interior = currentSettings.interiorOptions as Rack19InteriorOptions;
    const gridStepMm = gridStepFromOptions(options);

    if (mode === "z") {
      const restZ = settingsContext.restZ as number | undefined;
      const currentDepth = settingsContext.currentDepth as number | undefined;
      if (restZ === undefined || currentDepth === undefined) return null;

      const zSign = cornerAxisSign(restZ);
      const newDepth = dimensionFromCornerDelta(
        currentDepth,
        zSign * (newPosition.z - restZ),
        MIN_SIZE,
        gridStepMm,
      );
      const snappedPosition = newPosition.clone();
      snappedPosition.z = cornerAxisPosition(
        restZ,
        zSign,
        currentDepth,
        newDepth,
      );

      return {
        settingsUpdate: {
          interiorOptions: {
            ...interior,
            depth: newDepth,
          },
        },
        displayValues: {
          primary: {
            label: "D",
            value: newDepth,
            unit: "mm",
          },
        },
        snappedPosition: [
          snappedPosition.x,
          snappedPosition.y,
          snappedPosition.z,
        ],
      };
    } else {
      const restY = settingsContext.restY as number | undefined;
      const currentHeight = settingsContext.currentHeight as number | undefined;
      if (restY === undefined || currentHeight === undefined) return null;

      const cornerDelta = newPosition.y - restY;
      const newHeight = resolveRackHeight(
        currentHeight + 2 * cornerDelta,
        currentHeight,
        cornerDelta,
        gridStepMm,
      );
      const heValue = mmToHE(newHeight);
      const snappedPosition = newPosition.clone();
      snappedPosition.y = cornerAxisPosition(
        restY,
        1,
        currentHeight,
        newHeight,
      );

      return {
        settingsUpdate: {
          interiorOptions: {
            ...interior,
            height: newHeight,
          },
        },
        displayValues: {
          primary: {
            label: "H",
            value: newHeight,
            unit: "mm",
          },
          secondary: {
            label: "",
            value: heValue,
            unit: "HE",
          },
        },
        snappedPosition: [
          snappedPosition.x,
          snappedPosition.y,
          snappedPosition.z,
        ],
      };
    }
  },
};
